import React, { useRef } from "react";
import IconButton from "@mui/material/IconButton";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import DownloadIcon from "@mui/icons-material/Download";
import SaveIcon from "@mui/icons-material/Save";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import { useDAW } from "./DAWProvider";
import { exportBufferToWav } from "./audioBufferToWav";

function formatPlayhead(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toFixed(1).padStart(4, "0")}`;
}

export default function TransportControls() {
  const {
    isPlaying,
    togglePlay,
    numBars,
    setNumBars,
    pattern,
    clearPattern,
    exportSession,
    saveProject,
    loadProject,
    audioTracks,
    instrumentTracks,
    playheadPosition,
    bpm,
    canEditTransport,
  } = useDAW();

  const fileInputRef = useRef(null);
  const hasNotes = pattern.some((row) => row.some(Boolean));
  const hasContent = hasNotes || audioTracks.length > 0 || instrumentTracks.length > 0;

  const handleExportWav = async () => {
    if (!hasContent) return;
    const buffer = await exportSession();
    const blob = exportBufferToWav(buffer);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `beat-${Date.now()}.wav`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleOpenProject = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      loadProject(file);
      e.target.value = "";
    }
  };

  return (
    <div className="daw-transport">
      <IconButton
        onClick={togglePlay}
        className={`daw-transport__btn daw-transport__btn--play${isPlaying ? " daw-transport__btn--active-play" : ""}`}
        title={isPlaying ? "Stop" : "Play"}
        disabled={!canEditTransport}
      >
        {isPlaying ? <StopIcon /> : <PlayArrowIcon />}
      </IconButton>

      {/* Time display */}
      <div className="daw-transport__time">
        {formatPlayhead(playheadPosition)}
      </div>

      <div className="daw-transport__divider" />

      <div className="daw-transport__bars">
        <label className="daw-transport__label">Pattern:</label>
        <Select
          value={numBars}
          onChange={(e) => setNumBars(e.target.value)}
          disabled={!canEditTransport}
          size="small"
          sx={{
            color: "#ccc",
            fontSize: 13,
            height: 30,
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#444" },
            "& .MuiSvgIcon-root": { color: "#666" },
          }}
        >
          <MenuItem value={1}>1 bar</MenuItem>
          <MenuItem value={2}>2 bars</MenuItem>
          <MenuItem value={4}>4 bars</MenuItem>
          <MenuItem value={8}>8 bars</MenuItem>
          <MenuItem value={16}>16 bars</MenuItem>
          <MenuItem value={32}>32 bars</MenuItem>
        </Select>
      </div>

      <div className="daw-transport__divider" />

      <IconButton
        onClick={clearPattern}
        className="daw-transport__btn"
        title="Clear pattern"
        disabled={!hasNotes || !canEditTransport}
      >
        <DeleteSweepIcon />
      </IconButton>

      <div className="daw-transport__divider" />

      {canEditTransport && <IconButton
        onClick={saveProject}
        className="daw-transport__btn"
        title="Save project"
        disabled={!hasContent}
      >
        <SaveIcon />
      </IconButton>}

      {canEditTransport && <IconButton
        onClick={() => fileInputRef.current?.click()}
        className="daw-transport__btn"
        title="Open project"
      >
        <FolderOpenIcon />
      </IconButton>}

      {canEditTransport && <input
        ref={fileInputRef}
        type="file"
        accept=".json,.daw.json"
        style={{ display: "none" }}
        onChange={handleOpenProject}
      />}

      {hasContent && canEditTransport && (
        <IconButton
          onClick={handleExportWav}
          className="daw-transport__btn"
          title="Export WAV"
        >
          <DownloadIcon />
        </IconButton>
      )}
    </div>
  );
}
